import { Component, forwardRef, input, InputSignal, model, ModelSignal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SkeletonLoaderComponent } from "@shared/components/ui/skeleton-loader";

type TState = 'default' | 'disabled' | 'error';

@Component({
  selector: 'lib-switch',
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.css',
  imports: [FormsModule, SkeletonLoaderComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
})

export class SwitchComponent implements ControlValueAccessor {
  /**
 * The unique identifier of the input.
 * @default null
 */
  public readonly id: InputSignal<string> = input<string>();
  /**
    * The value of the input.
    * @default false
    */
  public readonly value: ModelSignal<boolean> = model<boolean>(false);

  /**
     * The state of the input.
     * @default 'default'
     * @type 'default' | 'disabled' | 'error'
     */
  public readonly state: ModelSignal<TState> = model<TState>('default');

  /**
   * A flag indicating if the input is required.\
   * @default false
   */
  public readonly required: InputSignal<boolean> = input<boolean>();

  public readonly skeleton = input<boolean>(false);

  /**
   * The function to be called when the input changes.
   */
  public onChange: any = () => { };

  /**
   * The function to be called when the input is touched.
   */
  public onTouched: any = () => { };

  /**
 * Initializes the component and ensures that the `value` is set correctly.
 */
  ngOnInit() {
    if (this.value() === undefined || this.value() === null) {
      this._value = false;
    }
  }

  /**
   * Updates the value when the input changes.
   * @returns void
   */
  public set _value(value: boolean) {
    this.value.set(value);
    this.onChange?.(this.value());
  }

  /**
   * Returns the current value of the input.
   * @returns string
   */
  public get _value(): boolean {
    return this.value();
  }

  /**
   * Writes a new value to the input.
   * @param value The new value to be written.
   * @returns void
   */
  public writeValue(value: boolean): void {
    this.value.set(value ?? false);
  }

  /**
   * Registers a function to be called when the input changes.
   * @param fn The function to be called when the input changes.
   * @returns void
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a function to be called when the input is touched.
   * @param fn The function to be called when the input is touched.
   * @returns void
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
